import React, { useState, useEffect } from 'react';
import { Trophy, Users, Calendar, Target, Award, Star, Crown, Zap, TrendingUp, Medal } from 'lucide-react';
import { database } from '../../Config/firebase';
import { ref, onValue, push, set, update } from '../../Config/matuRtdb';

const TournamentSystem = () => {
  const [tournaments, setTournaments] = useState([]);
  const [activeTournament, setActiveTournament] = useState(null);
  const [userStats, setUserStats] = useState({
    totalWins: 0,
    totalTournaments: 0,
    currentRank: 'Bronze',
    points: 0
  });
  const [leaderboard, setLeaderboard] = useState([]);
  const [showRegistration, setShowRegistration] = useState(false);

  // Torneos disponibles
  const availableTournaments = [
    {
      id: 'champions_league',
      name: 'Champions League Pro',
      description: 'El torneo más prestigioso del año',
      entryFee: 5000,
      prizePool: 1000000,
      maxParticipants: 64,
      currentParticipants: 32,
      startDate: '2024-02-15',
      endDate: '2024-03-15',
      status: 'registration',
      type: 'elimination',
      category: 'premium',
      requirements: {
        minLevel: 10,
        minWins: 50,
        vipOnly: true
      }
    },
    {
      id: 'weekend_warrior',
      name: 'Weekend Warrior',
      description: 'Torneo rápido de fin de semana',
      entryFee: 1000,
      prizePool: 50000,
      maxParticipants: 32,
      currentParticipants: 28,
      startDate: '2024-02-10',
      endDate: '2024-02-12',
      status: 'active',
      type: 'swiss',
      category: 'regular',
      requirements: {
        minLevel: 5,
        minWins: 10,
        vipOnly: false
      }
    },
    {
      id: 'rookie_cup',
      name: 'Copa Rookie',
      description: 'Para nuevos jugadores',
      entryFee: 0,
      prizePool: 10000,
      maxParticipants: 16,
      currentParticipants: 12,
      startDate: '2024-02-08',
      endDate: '2024-02-10',
      status: 'registration',
      type: 'round_robin',
      category: 'beginner',
      requirements: {
        minLevel: 1,
        minWins: 0,
        vipOnly: false
      }
    }
  ];

  // Tipos de torneos
  const tournamentTypes = {
    elimination: {
      name: 'Eliminación Simple',
      description: 'Pierdes y quedas eliminado',
      icon: '⚔️'
    },
    swiss: {
      name: 'Sistema Suizo',
      description: 'Juegas contra jugadores con puntuación similar',
      icon: '🎯'
    },
    round_robin: {
      name: 'Todos contra Todos',
      description: 'Juegas contra todos los participantes',
      icon: '🔄'
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'registration': return 'text-blue-400';
      case 'active': return 'text-green-400';
      case 'finished': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'registration': return 'Inscripciones Abiertas';
      case 'active': return 'En Progreso';
      case 'finished': return 'Finalizado';
      default: return 'Desconocido';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'premium': return 'bg-yellow-600';
      case 'regular': return 'bg-blue-600';
      case 'beginner': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  const registerForTournament = (tournament) => {
    // Verificar requisitos
    if (tournament.requirements.vipOnly && !userStats.isVIP) {
      alert('Este torneo es solo para miembros VIP');
      return;
    }

    if (userStats.totalWins < tournament.requirements.minWins) {
      alert(`Necesitas al menos ${tournament.requirements.minWins} victorias`);
      return;
    }

    // Registrar en Firebase
    const registrationRef = ref(database, `tournaments/${tournament.id}/participants`);
    push(registrationRef, {
      userId: localStorage.getItem('userUUID'),
      username: 'Usuario',
      registrationDate: Date.now(),
      status: 'active'
    });

    setShowRegistration(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-8 h-8 text-yellow-400" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              Sistema de Torneos
            </h1>
            <Crown className="w-8 h-8 text-purple-400" />
          </div>
          <p className="text-gray-300 text-lg">
            Compite en torneos épicos y gana premios increíbles
          </p>
        </div>

        {/* Stats del usuario */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-purple-800/50 backdrop-blur-sm rounded-xl p-4 border border-purple-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-gray-300">Victorias</span>
            </div>
            <div className="text-2xl font-bold text-purple-400">{userStats.totalWins}</div>
          </div>
          
          <div className="bg-blue-800/50 backdrop-blur-sm rounded-xl p-4 border border-blue-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-gray-300">Torneos</span>
            </div>
            <div className="text-2xl font-bold text-blue-400">{userStats.totalTournaments}</div>
          </div>
          
          <div className="bg-green-800/50 backdrop-blur-sm rounded-xl p-4 border border-green-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-300">Puntos</span>
            </div>
            <div className="text-2xl font-bold text-green-400">{userStats.points}</div>
          </div>
          
          <div className="bg-yellow-800/50 backdrop-blur-sm rounded-xl p-4 border border-yellow-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              <span className="text-sm text-gray-300">Rango</span>
            </div>
            <div className="text-2xl font-bold text-yellow-400">{userStats.currentRank}</div>
          </div>
        </div>

        {/* Torneos disponibles */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-400" />
            Torneos Disponibles
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableTournaments.map((tournament) => (
              <div 
                key={tournament.id}
                className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700/50 hover:border-purple-500/50 transition-all"
              >
                {/* Header del torneo */}
                <div className="relative">
                  <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-lg">{tournament.name}</h3>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(tournament.category)}`}>
                        {tournament.category.toUpperCase()}
                      </div>
                    </div>
                    <p className="text-sm text-gray-200">{tournament.description}</p>
                  </div>
                  
                  {/* Indicador de estado */}
                  <div className="absolute top-2 right-2">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium bg-black/50 ${getStatusColor(tournament.status)}`}>
                      {getStatusText(tournament.status)}
                    </div>
                  </div>
                </div>

                {/* Información del torneo */}
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Tipo:</span>
                    <span className="text-sm font-medium">
                      {tournamentTypes[tournament.type].icon} {tournamentTypes[tournament.type].name}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Participantes:</span>
                    <span className="text-sm font-medium">
                      {tournament.currentParticipants}/{tournament.maxParticipants}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Entrada:</span>
                    <span className="text-sm font-medium text-green-400">
                      ${tournament.entryFee.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Premio:</span>
                    <span className="text-sm font-medium text-yellow-400">
                      ${tournament.prizePool.toLocaleString()}
                    </span>
                  </div>

                  {/* Barra de progreso de participantes */}
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${(tournament.currentParticipants / tournament.maxParticipants) * 100}%` }}
                    />
                  </div>

                  {/* Requisitos */}
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <h4 className="text-sm font-medium mb-2">Requisitos:</h4>
                    <ul className="text-xs text-gray-300 space-y-1">
                      <li>• Nivel mínimo: {tournament.requirements.minLevel}</li>
                      <li>• Victorias mínimas: {tournament.requirements.minWins}</li>
                      {tournament.requirements.vipOnly && (
                        <li>• Solo miembros VIP</li>
                      )}
                    </ul>
                  </div>

                  {/* Botón de inscripción */}
                  <button
                    onClick={() => {
                      setActiveTournament(tournament);
                      setShowRegistration(true);
                    }}
                    disabled={tournament.status !== 'registration' || tournament.currentParticipants >= tournament.maxParticipants}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-all ${
                      tournament.status === 'registration' && tournament.currentParticipants < tournament.maxParticipants
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                        : 'bg-gray-600 cursor-not-allowed'
                    }`}
                  >
                    {tournament.status === 'registration' 
                      ? tournament.currentParticipants >= tournament.maxParticipants 
                        ? 'Completo' 
                        : 'Inscribirse'
                      : tournament.status === 'active' 
                        ? 'En Progreso' 
                        : 'Finalizado'
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leaderboard global */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-green-400" />
            Ranking Global
          </h2>
          
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
            <div className="space-y-4">
              {leaderboard.map((player, index) => (
                <div key={player.id} className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500 text-black' :
                      index === 1 ? 'bg-gray-400 text-black' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-gray-600 text-white'
                    }`}>
                      {index === 0 ? <Crown className="w-5 h-5" /> : index + 1}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <img src={player.avatar} alt={player.name} className="w-10 h-10 rounded-full" />
                      <div>
                        <div className="font-semibold">{player.name}</div>
                        <div className="text-sm text-gray-400">{player.rank}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-sm text-gray-300">Victorias</div>
                      <div className="font-bold">{player.wins}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-300">Puntos</div>
                      <div className="font-bold text-yellow-400">{player.points}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Modal de inscripción */}
        {showRegistration && activeTournament && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4">
              <h3 className="text-xl font-bold mb-4">Inscribirse en {activeTournament.name}</h3>
              
              <div className="space-y-4 mb-6">
                <div className="bg-gray-700/50 rounded-lg p-3">
                  <div className="flex justify-between mb-2">
                    <span>Entrada:</span>
                    <span className="text-green-400">${activeTournament.entryFee.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Premio máximo:</span>
                    <span className="text-yellow-400">${activeTournament.prizePool.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Participantes:</span>
                    <span>{activeTournament.currentParticipants}/{activeTournament.maxParticipants}</span>
                  </div>
                </div>
                
                <p className="text-sm text-gray-300">
                  ¿Estás seguro de que quieres inscribirte en este torneo? 
                  La entrada se cobrará de tu saldo.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRegistration(false)}
                  className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => registerForTournament(activeTournament)}
                  className="flex-1 py-2 px-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-lg transition-colors"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentSystem; 